import {
  EditOutlined,
  ExportOutlined,
  ImportOutlined,
  PlusCircleOutlined,
  QrcodeOutlined,
  ReloadOutlined,
  SwapOutlined,
} from '@ant-design/icons'
import { Button, TableSearch, Space, Dropdown, Select, Tooltip } from '@/components/ui'

interface AttendeeTableToolbarProps {
  searchTerm: string
  onSearchChange: (value: string) => void
  onSearch: () => void
  onReset: () => void
  onOpenFilter: () => void
  onOpenManageColumns: () => void
  onExport: (format: 'csv' | 'xlsx' | 'qr') => void
  isExportingXlsx?: boolean
  isExportingQr?: boolean
  onAddAttendee: () => void
  onOpenImport: () => void
  onOpenBulkUpdate: () => void
  selectedCount: number
  onBulkAction: (action: string) => void
  showPrinterSelector?: boolean
  printerOptions?: Array<{ label: string; value: string }>
  selectedPrinterUid?: string | null
  printerLoading?: boolean
  onPrinterChange?: (value: string) => void
  onRefreshPrinters?: () => void
  printerSetupRequired?: boolean
  printerSetupMessage?: string
}

export default function AttendeeTableToolbar({
  searchTerm,
  onSearchChange,
  onSearch,
  onReset,
  onOpenFilter,
  onOpenManageColumns,
  onExport,
  isExportingXlsx = false,
  isExportingQr = false,
  onAddAttendee,
  onOpenImport,
  onOpenBulkUpdate,
  selectedCount,
  onBulkAction,
  showPrinterSelector = false,
  printerOptions = [],
  selectedPrinterUid,
  printerLoading = false,
  onPrinterChange,
  onRefreshPrinters,
  printerSetupRequired = false,
  printerSetupMessage,
}: AttendeeTableToolbarProps) {
  const showPrinterSetupMessage = !printerLoading && (printerSetupRequired || printerOptions.length === 0)

  return (
    <div className="mb-4 space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <TableSearch
            value={searchTerm}
            onChange={onSearchChange}
            onSearch={onSearch}
            onReset={onReset}
            onFilter={onOpenFilter}
            showFilter={true}
          />
        </div>
        <Space size="small" wrap>
          <Button variant="tertiary" icon={<EditOutlined />} onClick={onOpenManageColumns}>
            Manage Fields
          </Button>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'import',
                  label: 'Import from CSV',
                  icon: <ImportOutlined />,
                  onClick: onOpenImport,
                },
                {
                  type: 'divider',
                },
                {
                  key: 'csv',
                  label: 'Export as CSV',
                  icon: <ExportOutlined />,
                  onClick: () => onExport('csv'),
                },
                {
                  key: 'xlsx',
                  label: isExportingXlsx ? 'Generating Excel…' : 'Export as Excel',
                  icon: <ExportOutlined />,
                  disabled: isExportingXlsx,
                  onClick: () => onExport('xlsx'),
                },
                {
                  key: 'qr',
                  label: isExportingQr ? 'Preparing QR codes…' : 'Export QR codes (ZIP)',
                  icon: <QrcodeOutlined />,
                  disabled: isExportingQr,
                  onClick: () => onExport('qr'),
                },
              ],
            }}
            trigger={['click']}
          >
            <Button variant="tertiary" icon={<SwapOutlined />}>Import/Export</Button>
          </Dropdown>
          <Button variant="secondary" icon={<PlusCircleOutlined />} onClick={onAddAttendee}>
            Add Attendee
          </Button>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'edit_fields',
                  label: 'Edit Fields',
                  icon: <EditOutlined />,
                  onClick: onOpenBulkUpdate,
                },
                {
                  type: 'divider',
                },
                {
                  key: 'export_selected',
                  label: 'Export Selected',
                  onClick: () => onBulkAction('export_selected'),
                },
                {
                  key: 'approve',
                  label: 'Mark as Approved',
                  onClick: () => onBulkAction('approve'),
                },
                {
                  key: 'reject',
                  label: 'Mark as Rejected',
                  onClick: () => onBulkAction('reject'),
                },
                {
                  key: 'cancel',
                  label: 'Mark as Cancelled',
                  onClick: () => onBulkAction('cancel'),
                },
              ],
            }}
            trigger={['click']}
            disabled={selectedCount === 0}
          >
            <Button variant="primary" icon={<EditOutlined />} disabled={selectedCount === 0}>
              Bulk Update ({selectedCount})
            </Button>
          </Dropdown>
        </Space>
      </div>

      {showPrinterSelector && (
        <div className="flex justify-end">
          <div className="flex items-center gap-2">
            <Select
              placeholder={showPrinterSetupMessage ? 'Pair print station' : 'Select agent printer'}
              value={selectedPrinterUid || undefined}
              options={printerOptions}
              onChange={(value) => onPrinterChange?.(value)}
              className="min-w-[240px]"
              disabled={showPrinterSetupMessage}
            />
            <Tooltip title="Refresh printers">
              <span>
                <Button
                  variant="tertiary"
                  icon={<ReloadOutlined />}
                  loading={printerLoading}
                  onClick={onRefreshPrinters}
                  className="px-2"
                >
                  <span className="sr-only">Refresh printers</span>
                </Button>
              </span>
            </Tooltip>
            {showPrinterSetupMessage && (
              <span className="text-xs text-amber-700">
                {printerSetupMessage ||
                  'Open CloudExpo Print Agent, enter the server URL and pairing code, then select a printer and profile.'}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
